import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './SignUp.css';
import { useSignUpContext } from './provider/SignUpProvider';
import { withAsyncImport } from '../../withAsyncImport';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { IDigitalBankTerms, IRate, ITerm } from '../../types/shared/DigitalBankTerms';
import { useTermRatesContext } from '../../providers/TermRates/TermRates';

const Original = withAsyncImport(() => import('./variants/Original'));
const Slim = withAsyncImport(() => import('./variants/Slim'));
const CDSavings = withAsyncImport(() => import('./variants/CDSavings'));

const NewSignUp = withAsyncImport(() => import('./variants/re-design/NewSignUp'));

interface IIcon {
    iconPath: string;
    iconAltText: string;
}

interface IRadio {
    labelText: string;
    idValueData: string;
}

interface IBulletPoint {
    value: string;
}

interface ITermRates {
    text: string;
    url: string;
}

interface ITermsData {
    featuredApy?: string;
    featuredTerm?: string;
    subtitle?: string;
    termsRates?: ITermRates[];
    buttonLink?: string;
}

interface ISignUpProps extends MappedComponentProperties {
    author?: boolean;
    variantType?: 'originalVariant'| 'cdVariant' | 'savingsVariant' | 'cdSlimVariant';
    usingDigitalBankTermsProvider?: boolean;
    backgroundImgPath?: string;
    headerText?: string;
    lifestyleImgDesk?: string;
    lifestyleImgMob?: string;
    paragraphText?: string;
    disclaimerText?: string;
    termsLabel?: string;
    termsLink?: string;
    apiFormID?: string;
    apiErrorCode?: string;
    campaignName?: string;
    formTitle?: string;
    formQuestionText?: string;
    bulletIcon?: IIcon;
    bulletPoint?: IBulletPoint[];
    termsRates?: ITermRates[];
    buttonText?: string;
    buttonId?: string;
    buttonType?: 'solid' | 'link' | 'ghosted-light' | 'ghosted-dark';
    buttonIcon?: 'lock' | 'chevron' | 'back'| '';
    annotateText?: string;
    overlayDots?: string;
    formLabels?: IRadio[];
    promotionalImgPath?: string;
    promotionalMobileImgPath?: string;
    cornerImgPath?: string;
    confirmText?: string;
    submitButtonText?: string;
    showNewDesign?: boolean;
}

export const SignUpEditConfig = {
    emptyLabel: 'Sign Up',
    isEmpty: (props: PropsWithChildren<ISignUpProps>) => {
        return !props || !props.termsRates?.length;
    },
};

const SignUp = (props: PropsWithChildren<ISignUpProps>): JSX.Element => {
    const { setTerm, setApiFormID, setCampaignName, setIsAuthor, setApiErrorCode } = useSignUpContext();
    const [rateData, setRateData] = useState<ITermsData>({ ...props });
    const { getFeaturedItem, termRates } = useTermRatesContext();

    const getDigitalBankTermType = (): [keyof IDigitalBankTerms|null, keyof IDigitalBankTerms|null] => {
        const pathname = window.location.href;
        if (pathname.includes('deposits/cd')) {
            return ['jumboCdTerm', 'jumboCd'];
        } else if (pathname.includes('deposits/bump-cd')) {
            return ['bumpCdTerm', 'bumpUpCd'];
        } else if (pathname.includes('deposits/savings')) {
            return ['highYieldSavingsTerm', 'highYieldSavings'];
        }

        return [null, null];
    };

    const buildDigitalBankFeaturedRateData = (termData: IDigitalBankTerms, termKey: keyof IDigitalBankTerms|null, rateKey: keyof IDigitalBankTerms|null) => {
        if (!termKey || !rateKey) {
            return;
        }

        const featuredRateContent: IRate|undefined = (termData[rateKey] as IRate[])?.find((term) => term.featuredRate);

        if (!featuredRateContent || !Object.keys(featuredRateContent).length) {
            return;
        }

        const featuredRate = [{
            text: props.termsRates && props.termsRates[0].text ?
                props.termsRates[0].text
                    .replace(/(\d{1,2})( months)/gi, featuredRateContent.term + '$2')
                    .replace(/(\d{1,2}.\d{1,4})(%)/gi, featuredRateContent.apy + '$2') : '',
            url: featuredRateContent.url as string,
        }];

        setRateData({
            featuredApy: featuredRateContent.apy,
            featuredTerm: featuredRateContent.term ? `${featuredRateContent.term}-Month` : '',
            subtitle: (termData[termKey] as ITerm).subtitle,
            buttonLink: featuredRateContent.url,
            termsRates: featuredRate,
        });
    };

    useEffect(() => {
        const digitalBankTerms: IDigitalBankTerms = JSON.parse(PathUtils.getMetaPropertyValue('digitalBankTerms') as string);
        if (props.usingDigitalBankTermsProvider && Object.keys(digitalBankTerms).length) {
            const [termType, rateListKey] = getDigitalBankTermType();

            if (termType && (digitalBankTerms[termType] as ITerm)?.subtitle) {
                buildDigitalBankFeaturedRateData(digitalBankTerms, termType, rateListKey);
            }
        } else if (!props.usingDigitalBankTermsProvider && termRates && Object.keys(termRates).length) {
            const termTypes = getDigitalBankTermType();
            const featuredItem = getFeaturedItem(termTypes[1] as 'jumboCd' | 'bumpUpCd' | 'highYieldSavings');
            
            const featuredRate = [{
                text: props.termsRates && props.termsRates[0].text ?
                    props.termsRates[0].text
                        .replace(/(\d{1,2})( months)/gi, featuredItem.term + '$2')
                        .replace(/(\d{1,2}.\d{1,4})(%)/gi, featuredItem.apy + '$2') : '',
                url: featuredItem.url as string,
            }];

            setRateData({
                featuredApy: featuredItem.apy,
                featuredTerm: featuredItem.term ? `${featuredItem.term}-Month` : '',
                subtitle: termTypes[0] ? (digitalBankTerms[termTypes[0]] as ITerm).subtitle : '',
                buttonLink: featuredItem.url,
                termsRates: featuredRate,
            });
        }
    }, [termRates]);

    useEffect(() => {
        if (props.author) {
            setIsAuthor(props.author);
        }

        if (props.apiErrorCode) {
            setApiErrorCode(props.apiErrorCode);
        }

        setCampaignName(props.campaignName || 'SummerSlam');
        setApiFormID(props.apiFormID || '20');

    }, []);

    useEffect(() => {
        if (props.variantType === 'savingsVariant' && props.termsRates && props.termsRates[0]) {
            setTerm(props.termsRates[0].text);
        }
    }, [props.variantType]);

    const generateSignUp = useCallback(() => {
        if (props.showNewDesign) {
            return <NewSignUp {...{...props, ...rateData}}/>
        } else {
            switch (props.variantType) {
                case 'cdSlimVariant':
                    return <Slim {...{ ...props, ...rateData }} />;
                case 'originalVariant':
                    return <Original {...props }/>;
                default:
                    return <CDSavings {...props}/>;
            }
        }
    }, [rateData]);

    return (
        <>
            {generateSignUp()}
        </>
    );
};

export default SignUp;
